import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  type Signal,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { FormsModule } from '@angular/forms';
import {
  ListCommentsSortEnum,
  type Comment,
  type CommentAttachment,
  type CommentReaction,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { CommentAttachmentsService } from './services/comment-attachments.service';
import { CommentsService } from './services/comments.service';
import { COMMENTS_CAPABILITIES } from './services/comments-capabilities';
import {
  COMMENTS_EVENTS,
  type CommentEvent,
  type CommentCreatedEvent,
  type CommentDeletedEvent,
  type CommentEditedEvent,
  type CommentReactedEvent,
  type CommentRestoredEvent,
  type CommentUnreactedEvent,
} from './services/comments-events';
import { CommentRowComponent } from './components/comment-row.component';
import { CommentComposerComponent } from './components/comment-composer.component';
import { CommentEditHistoryDialogComponent } from './components/comment-edit-history-dialog.component';

type Sort =
  | typeof ListCommentsSortEnum.ThreadedDesc
  | typeof ListCommentsSortEnum.ThreadedAsc
  | typeof ListCommentsSortEnum.FlatDesc
  | typeof ListCommentsSortEnum.FlatAsc;

interface SortOption {
  value: Sort;
  labelKey: string;
}

/**
 * Threaded comments list component. Drop in on any target via `[targetType]` + `[targetId]`.
 *
 * F1 ships read-only: list + sort + load-more + tombstone rendering. The composer, edit / delete /
 * restore actions, edit history, read-tracking, deep links, and SSE wiring land in F2–F6 — see
 * `docs/plans/comments/frontend_plan.md`.
 *
 * The component owns the in-memory list state. Each new sort triggers a fresh load from page 0;
 * "load more" appends the next page to the existing list.
 */
@Component({
  selector: 'tf-comments',
  standalone: true,
  imports: [
    TranslateModule,
    FormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    SelectModule,
    ToastModule,
    ConfirmDialogModule,
    CommentRowComponent,
    CommentComposerComponent,
    CommentEditHistoryDialogComponent,
  ],
  providers: [MessageService, ConfirmationService, DialogService],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentsComponent implements OnInit, OnDestroy {
  private readonly commentsService = inject(CommentsService);
  private readonly attachmentsService = inject(CommentAttachmentsService);
  private readonly capabilities = inject(COMMENTS_CAPABILITIES);
  private readonly events = inject(COMMENTS_EVENTS);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);

  /** Listening to the rendered list element so the IntersectionObserver below knows what to scope. */
  protected readonly listEl = viewChild<ElementRef<HTMLElement>>('listEl');

  protected readonly canCreateComments = computed(() => this.capabilities.canCreateComments());
  protected readonly canModerateComments = computed(() => this.capabilities.canModerateComments());
  protected readonly currentUserId = computed(() => this.capabilities.currentUserId());

  /**
   * Family-prefixed target identifier (`"co:<slug>"` for custom-object instances; future
   * families will get their own prefix). Wire convention is shared by every comments endpoint,
   * so the composer reuses the same signal — no second enum to coerce against.
   */
  readonly targetType = input.required<string>();
  readonly targetId = input.required<string>();

  /**
   * Optional deep-link target. When set the component fetches `getPageInfo`, loads pages 0..target
   * (so the row is in the visible list), scrolls it into view, and applies a 2.5 s `--focused`
   * highlight. Passing `null` (the default) is a no-op. The page-context shell in
   * `RecordPageComponent` mirrors this from `?commentId=…` so URL changes during the session
   * re-trigger the focus.
   */
  readonly commentId = input<string | null>(null);

  protected readonly composerTargetType = this.targetType;

  /**
   * Emitted when `unread_count` changes so the parent (e.g. the instance-detail tab) can hang
   * a badge on its own tab header. F1 will always emit `0` because read-tracking is wired in
   * F4 — but we expose the output now so the contract is stable.
   */
  readonly unreadCountChange = output<number>();

  protected readonly sortOptions: SortOption[] = [
    { value: ListCommentsSortEnum.ThreadedDesc, labelKey: 'COMMENTS.SORT.THREADED_DESC' },
    { value: ListCommentsSortEnum.ThreadedAsc, labelKey: 'COMMENTS.SORT.THREADED_ASC' },
    { value: ListCommentsSortEnum.FlatDesc, labelKey: 'COMMENTS.SORT.FLAT_DESC' },
    { value: ListCommentsSortEnum.FlatAsc, labelKey: 'COMMENTS.SORT.FLAT_ASC' },
  ];

  protected readonly selectedSort = signal<Sort>(ListCommentsSortEnum.ThreadedDesc);

  /**
   * Set once the user's persisted sort preference (or absence thereof) has been resolved. The
   * initial list fetch waits for this so we don't waste a request on the in-component default
   * before swapping to the persisted value. When the prefs API itself fails, we still flip the
   * flag (sort prefs are non-critical — degrade to the default rather than block the UI).
   */
  protected readonly prefsResolved = signal(false);

  protected readonly comments = signal<Comment[]>([]);
  protected readonly currentPage = signal(0);
  protected readonly totalPages = signal(0);
  protected readonly loading = signal(false);
  protected readonly loadingMore = signal(false);
  protected readonly hasError = signal(false);
  /**
   * Flips to true once a `loadFirstPage` cycle has started **and** completed (success or fail)
   * for the current target+sort. Used by the deep-link effect to avoid running the resolver
   * before the list has had a chance to load (a race that previously triggered the
   * "Comment not available" toast even though the row was on page 0). Reset to false at the
   * start of every fresh first-page load.
   */
  private readonly firstLoadCompleted = signal(false);

  /** Id of the comment whose inline reply composer is currently open, or null when none. */
  protected readonly replyingTo = signal<string | null>(null);

  /** Id of the comment currently in inline edit mode, or null when none. */
  protected readonly editingId = signal<string | null>(null);

  /** Id of the comment whose edit-history dialog is open, or null when closed. */
  protected readonly historyOpenForId = signal<string | null>(null);
  protected readonly historyVisible = computed(() => this.historyOpenForId() !== null);

  /**
   * Whether the main "New comment" composer is expanded. Stays closed by default — the user
   * clicks the "New comment" button in the header to open it. We auto-collapse on a successful
   * post so the next "New comment" click starts from a fresh empty draft.
   */
  protected readonly composerOpen = signal(false);

  /**
   * IntersectionObserver for the auto-mark-read-on-scroll path. Created lazily after the list
   * mounts, kept alive across page-loads, and disconnected on destroy. Watching every row
   * element by `data-comment-id` keeps the implementation stable across sort / pagination
   * churn.
   */
  private autoReadObserver: IntersectionObserver | null = null;
  /** Comment ids currently fully visible in the scroll viewport. */
  private readonly visibleIds = new Set<string>();
  /** Cursor we've already advanced to — avoids re-firing for ids whose `createdAt` is older. */
  private lastReadCursorAt: number | null = null;
  /** Pending debounce timer for the next mark-read-until call. */
  private autoReadTimer: ReturnType<typeof setTimeout> | null = null;
  /** Idle delay before a stable visible-set triggers a mark-read-until call. */
  private static readonly AUTO_READ_DEBOUNCE_MS = 1500;

  /**
   * Local-vs-remote SSE dedup. When the user performs a write the optimistic local update lands
   * first; the matching SSE event then arrives a few ms later and would re-apply the same change
   * (or, on CREATED, trigger a redundant `getById` round trip). Adding the affected `comment_id`
   * here on local action and skipping events whose id is in the set within
   * {@link SSE_DEDUP_WINDOW_MS} keeps the two paths from fighting.
   */
  private readonly recentlyAuthoredIds = new Map<string, number>();
  private static readonly SSE_DEDUP_WINDOW_MS = 5000;

  /**
   * Comment ids whose `<li>` should render with the entry-animation class. Cleared after the
   * animation duration so the same row can re-trigger if it arrives again later (which shouldn't
   * happen in practice, but keeps the state machine simple).
   */
  protected readonly arrivedIds = signal<ReadonlySet<string>>(new Set());
  private static readonly ARRIVAL_ANIMATION_MS = 600;

  /** Polite live-region announcement string, updated when a non-self CREATED event arrives. */
  protected readonly liveRegionMessage = signal('');

  /**
   * Plain-text mirror of whichever composer or edit form is currently mounted. Updated by the
   * `(draftChange)` outputs on `<tf-comment-composer>` and `<tf-comment-edit-form>`. Wraps to
   * an empty string when nothing is open. Read by the host (`RecordPageComponent`) via the
   * public `activeDraft()` accessor so the AI assistant page-context can show the assistant
   * what the user is currently typing — read-only here; the AI cannot mutate this. (See
   * TF-1737 for the future "local browser tools" capability that would let the assistant
   * write into the editor.)
   */
  private readonly currentDraftText = signal<string>('');

  /**
   * Comment id currently rendered with the deep-link focus treatment (left accent bar pulse +
   * brief background fade). Cleared automatically after {@link FOCUS_HIGHLIGHT_MS}. The value
   * comes from `commentId()` after the deep-link resolver lands the row in the visible list.
   */
  protected readonly focusedCommentId = signal<string | null>(null);
  /**
   * Stable key for the last focus attempt — `<commentId>::<sort>::<targetType>::<targetId>`.
   * Re-running the resolver on the same key would just refire the (still-correct) highlight, so
   * we skip; changing sort or target invalidates the key and a fresh resolve runs.
   */
  private lastFocusKey: string | null = null;
  private static readonly FOCUS_HIGHLIGHT_MS = 2500;

  protected readonly isThreaded = computed(() => this.selectedSort().startsWith('threaded:'));

  protected readonly hasMore = computed(() => this.currentPage() + 1 < this.totalPages());
  protected readonly isEmpty = computed(
    () => !this.loading() && !this.hasError() && this.comments().length === 0,
  );

  /**
   * Per-row thread metadata derived from the flat list returned by the server. The threaded
   * connector lines in `<tf-comment-row>` (`hasReplies`, `isReply`, `isLastReply`) are drawn
   * from these flags — see the prototype's `.cmt.has-replies` / `.cmt-reply` / `.is-last-reply`
   * pseudo-elements at `.tmp/comments-prototype/.../comments.css`.
   *
   * **Flat sort modes deliberately suppress every threading flag** — the user picked a flat
   * view, so all rows render as siblings regardless of their `parent_comment_id`. Indents,
   * connector lines, and the per-row Reply button are all gated off `isThreaded()`.
   *
   *  - `hasReplies`: this is a root and the next item in the list belongs to the same thread.
   *  - `isReply`:    this comment has a non-null parent.
   *  - `isLastReply`: this is a reply and the next item starts a different thread (or there
   *                   is no next item) — used to trim the vertical trunk at the avatar center.
   *  - `isThreadStart`: this row begins a new thread (root with no preceding row in the same
   *                     thread) — used by the parent list to hang a top divider.
   */
  protected readonly viewModel = computed(() => {
    const list = this.comments();
    const isThreaded = this.isThreaded();
    // Lookup map for resolving "in reply to @X" labels in flat mode — the parent comment may
    // not be visually adjacent there, so the indicator carries the relationship.
    const byId = new Map(list.map((c) => [c.id, c]));

    return list.map((c, i) => {
      const next = list[i + 1];
      const prev = list[i - 1];
      const isReply = c.parentCommentId != null;

      // Threading flags drive the indent + connector lines; only meaningful in threaded mode.
      // Flat mode forces them all off so every row renders as a sibling.
      const threadingFlags = isThreaded
        ? {
            isReply,
            isLastReply: isReply && (!next || next.rootCommentId !== c.rootCommentId),
            hasReplies: !isReply && !!next && next.rootCommentId === c.rootCommentId,
            isThreadStart: !isReply && (!prev || prev.rootCommentId !== c.rootCommentId),
          }
        : { isReply: false, isLastReply: false, hasReplies: false, isThreadStart: false };

      // "in reply to @Author" badge — flat mode only. In threaded mode the connector lines
      // already convey the parent relationship visually, so the badge would be redundant.
      // Resolves the parent's author from the loaded list; null when the parent isn't on a
      // currently-loaded page (the row still renders, just without the parent's name).
      const replyToAuthor =
        !isThreaded && isReply && c.parentCommentId
          ? (byId.get(c.parentCommentId)?.author ?? null)
          : null;

      return {
        comment: c,
        ...threadingFlags,
        replyToAuthor,
      };
    });
  });

  constructor() {
    // Initial load + reload whenever the target or sort changes. Triggering off `selectedSort`
    // here means `onSortChange` only needs to flip the signal — no separate handler required.
    // The effect waits on `prefsResolved` so we issue the initial request with the user's
    // persisted sort, not the default.
    effect(() => {
      if (!this.prefsResolved()) return;
      const targetType = this.targetType();
      const targetId = this.targetId();
      const sort = this.selectedSort();
      if (!targetType || !targetId) return;
      void this.loadFirstPage(targetType, targetId, sort);
    });

    // Auto-mark-read-on-scroll wiring. Reattach observation whenever the list re-renders so
    // newly-loaded pages (load-more / sort change) are observed too. The observer itself is
    // long-lived; we just call `observe()` for the new row elements and rely on the previous
    // `<li>` nodes being torn down by Angular (they auto-unobserve).
    effect(() => {
      // Re-fire on every list change so observation tracks the current row set.
      this.comments();
      const listEl = this.listEl()?.nativeElement;
      if (!listEl) return;
      this.attachAutoReadObserver(listEl);
    });

    // SSE pipeline. Filter every event by `(target_type, target_id)` against the current view —
    // events for other records on the same stream are dropped here. Then dispatch by kind.
    this.events.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.onSseEvent(event));

    // Deep-link resolver. Re-runs whenever `commentId` (input), `loading` (first-page state),
    // or the sort/target (page-info parameters) change. Guarded by `lastFocusKey` so a stable
    // input doesn't refire the highlight on unrelated signal updates (e.g. read-state churn).
    effect(() => {
      const id = this.commentId();
      if (!id) {
        this.focusedCommentId.set(null);
        this.lastFocusKey = null;
        return;
      }
      // Wait until the first list page has actually loaded. Reading `loading()` alone isn't
      // enough — at component-mount time `loading` is still its initial `false` (loadFirstPage
      // hasn't started yet), and the resolver would race ahead of the list and trip the
      // not-found fallback even though the row is about to land on page 0.
      if (!this.firstLoadCompleted() || this.loading()) return;
      const targetType = this.targetType();
      const targetId = this.targetId();
      if (!targetType || !targetId) return;
      const key = `${id}::${this.selectedSort()}::${targetType}::${targetId}`;
      if (this.lastFocusKey === key) return;
      this.lastFocusKey = key;
      this.resolveDeepLink(id);
    });
  }

  ngOnInit(): void {
    // Resolve the user's persisted sort preference on mount. The list-fetch effect waits on
    // `prefsResolved` so the initial request goes out with the correct sort. A failure (no
    // preference set, network error, etc.) still flips the flag so the UI degrades to the
    // in-component default rather than spinning forever.
    void this.commentsService.loadSortPreference().then((stored) => {
      if (stored) this.selectedSort.set(stored as Sort);
      this.prefsResolved.set(true);
    });
  }

  /** Persist + apply the new sort. Persistence is best-effort — the local change is applied
   * immediately so the user sees the list reorder without waiting on the PATCH round-trip. */
  protected onSortChange(value: Sort): void {
    this.selectedSort.set(value);
    void this.commentsService.saveSortPreference(value);
  }

  protected onLoadMore(): void {
    if (!this.hasMore() || this.loadingMore()) return;
    void this.loadNextPage();
  }

  protected onReplyRequested(commentId: string): void {
    this.replyingTo.set(this.replyingTo() === commentId ? null : commentId);
  }

  protected onReplyCancelled(): void {
    this.replyingTo.set(null);
  }

  /**
   * Toggles a row into inline edit mode. Clicking Edit on the same row twice closes the form
   * (mirrors the Reply toggle behavior). Opening edit on a different row implicitly closes any
   * open reply composer on this row to keep one-active-form-per-row.
   */
  protected onEditRequested(commentId: string): void {
    if (this.editingId() === commentId) {
      this.editingId.set(null);
      return;
    }
    this.editingId.set(commentId);
    this.replyingTo.set(null);
  }

  protected onEditCancelled(): void {
    this.editingId.set(null);
  }

  /**
   * Patches the in-memory list with the freshly-edited comment. The server returns the canonical
   * post-edit shape (`isEdited`, `isModerated`, `lastEditedAt` all reflect the moderator-vs-author
   * path), so we just splice it in by id.
   */
  protected onEditSaved(updated: Comment): void {
    this.editingId.set(null);
    this.markRecentlyAuthored(updated.id);
    this.comments.update((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c)),
    );
  }

  /**
   * Confirms via PrimeNG's `ConfirmDialog` and on accept performs an optimistic soft-delete:
   * the row immediately flips to its tombstone state in the local list, then the API call goes
   * out. On failure we restore the previous state and surface a toast — the alternative
   * (block + spinner) caused noticeable UI freezes when the request was slow on flaky links.
   */
  protected onDeleteRequested(commentId: string): void {
    const previous = this.comments().find((c) => c.id === commentId);
    if (!previous) return;
    this.confirmationService.confirm({
      header: this.translate.instant('COMMENTS.DELETE.CONFIRM_TITLE'),
      message: this.translate.instant('COMMENTS.DELETE.CONFIRM_BODY'),
      acceptLabel: this.translate.instant('COMMENTS.DELETE.CONFIRM_OK'),
      rejectLabel: this.translate.instant('GENERAL.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      icon: 'pi pi-exclamation-triangle',
      accept: () => void this.runDelete(previous),
    });
  }

  private async runDelete(previous: Comment): Promise<void> {
    // Optimistic: flip the local copy to the tombstone shape.
    const tombstoned: Comment = {
      ...previous,
      isDeleted: true,
      isDeletedByModerator: previous.author.id !== this.currentUserId(),
    };
    this.comments.update((prev) => prev.map((c) => (c.id === previous.id ? tombstoned : c)));
    if (this.editingId() === previous.id) this.editingId.set(null);
    if (this.replyingTo() === previous.id) this.replyingTo.set(null);
    this.markRecentlyAuthored(previous.id);
    try {
      await this.commentsService.delete(previous.id);
    } catch {
      this.comments.update((prev) => prev.map((c) => (c.id === previous.id ? previous : c)));
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.DELETE.ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.DELETE.ERROR_DETAIL'),
        life: 5000,
      });
    }
  }

  /**
   * Restore is moderator-only and hits the server before patching the local list — unlike soft-
   * delete we don't have a clean "previous body" to flip to optimistically (the tombstone view
   * had no body), so we wait for the server's canonical post-restore shape.
   */
  protected async onRestoreRequested(commentId: string): Promise<void> {
    try {
      const restored = await this.commentsService.restore(commentId);
      this.markRecentlyAuthored(commentId);
      this.comments.update((prev) => prev.map((c) => (c.id === commentId ? restored : c)));
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.RESTORE.ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.RESTORE.ERROR_DETAIL'),
        life: 5000,
      });
    }
  }

  protected onViewHistoryRequested(commentId: string): void {
    this.historyOpenForId.set(commentId);
  }

  /**
   * Receives draft text from the currently-open composer or edit form (`<tf-comment-composer>`
   * and `<tf-comment-edit-form>` both fire `draftChange` on every body keystroke). The
   * aggregated text is exposed via {@link activeDraft} so the host can surface it to the AI
   * assistant page-context.
   */
  protected onDraftChange(text: string): void {
    this.currentDraftText.set(text);
  }

  // ─── Public read-only accessors (consumed by the host's AI page-context factory) ─────

  /**
   * Stable snapshot of the loaded comment list. Read by the host (`RecordPageComponent`'s
   * AI page-context factory) to derive `visible_rows`. Returns the same `Comment[]` the
   * component renders internally — exposed read-only so external consumers can react to
   * changes via `viewChild(CommentsComponent)` + signal effects.
   */
  readonly loadedComments: Signal<readonly Comment[]> = this.comments.asReadonly();

  /** Currently-focused comment id (deep-link target), or `null` when no row is highlighted. */
  readonly focusedComment: Signal<string | null> = this.focusedCommentId.asReadonly();

  /** Sort the user has selected — used by the page-context to surface the active filter. */
  readonly currentSort: Signal<Sort> = this.selectedSort.asReadonly();

  /**
   * Snapshot of the currently-active draft (composer or edit-form), or `null` when no
   * composer is mounted. The kind discriminates the three places a draft can live; `id`
   * stays `null` for new-comment drafts (no comment id yet), and is the comment id for
   * inline edits.
   */
  readonly activeDraft: Signal<{
    kind: 'create' | 'reply' | 'edit';
    id: string | null;
    text: string;
  } | null> = computed(() => {
    const text = this.currentDraftText();
    if (this.composerOpen()) {
      return { kind: 'create', id: null, text };
    }
    const replyParent = this.replyingTo();
    if (replyParent) {
      return { kind: 'reply', id: replyParent, text };
    }
    const editing = this.editingId();
    if (editing) {
      return { kind: 'edit', id: editing, text };
    }
    return null;
  });

  /**
   * Builds the deep-link URL for [commentId] (`${origin}${pathname}?tab=comments&commentId=…`)
   * and copies it to the clipboard, toasting on success or failure. The URL preserves any
   * other query params already on the page (sort, filters from elsewhere) so the recipient
   * lands in the same view as the user who shared.
   */
  protected async onCopyLink(commentId: string): Promise<void> {
    const url = new URL(globalThis.location.href);
    // Force the params we know we want; leave the rest as-is. Replacing both is intentional —
    // a "view this comment in the comments tab" link should always open the comments tab.
    url.searchParams.set('tab', 'comments');
    url.searchParams.set('commentId', commentId);
    const link = url.toString();
    try {
      await navigator.clipboard.writeText(link);
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('COMMENTS.COPY_LINK.SUCCESS_TITLE'),
        detail: this.translate.instant('COMMENTS.COPY_LINK.SUCCESS_DETAIL'),
        life: 2500,
      });
    } catch {
      // Insecure context (no HTTPS / no localhost) or user-denied permission. Surface as a
      // soft error — the user can still copy from the address bar after they manually navigate.
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.COPY_LINK.ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.COPY_LINK.ERROR_DETAIL'),
        life: 4000,
      });
    }
  }

  /**
   * Per-row Mark-as-read action. Optimistically flips just this row to read; on failure rolls
   * back. Doesn't advance `lastReadCursorAt` because the cursor semantic is "newest-seen", and
   * marking a single older row as read shouldn't fast-forward the cursor.
   */
  protected async onMarkRead(commentId: string): Promise<void> {
    const before = this.comments().find((c) => c.id === commentId);
    if (!before?.isUnread) return;
    this.flipRead(commentId, false);
    try {
      await this.commentsService.markRead(commentId);
    } catch {
      this.flipRead(commentId, true);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.MARK_READ.ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.MARK_READ.ERROR_DETAIL'),
        life: 5000,
      });
    }
  }

  /** Per-row Mark-as-unread action — mirror of `onMarkRead` for the unread direction. */
  protected async onMarkUnread(commentId: string): Promise<void> {
    const before = this.comments().find((c) => c.id === commentId);
    if (!before || before.isUnread) return;
    this.flipRead(commentId, true);
    try {
      await this.commentsService.markUnread(commentId);
    } catch {
      this.flipRead(commentId, false);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.MARK_UNREAD.ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.MARK_UNREAD.ERROR_DETAIL'),
        life: 5000,
      });
    }
  }

  /**
   * Reaction toggle dispatched by `<tf-reactions-bar>` (either a chip click on an existing
   * aggregate entry, or an emoji pick from the popover). The decision between add / remove is
   * made here from the current `youReacted` flag — the bar itself doesn't know. Optimistic
   * update lands first; the server response replaces the row with the canonical aggregate; on
   * error we roll back and toast.
   *
   * The user's own action is suppressed from the matching SSE event via {@link recentlyAuthoredIds}
   * so we don't double-apply the toggle. The reaction events also carry `reactorId`, which lets
   * the SSE handler skip self-authored fan-out without consulting that map.
   */
  protected async onReactionToggled(payload: { commentId: string; emoji: string }): Promise<void> {
    const { commentId, emoji } = payload;
    const before = this.comments().find((c) => c.id === commentId);
    if (!before || before.isDeleted) return;
    const userId = this.currentUserId();
    if (!userId) return;

    const existing = before.reactions.find((r) => r.emoji === emoji);
    const willRemove = existing?.youReacted === true;
    // For remove we need the caller's opaque reaction id, set by the server on the aggregate
    // entry when `you_reacted == true`. If it's missing the row is in a transient state we can't
    // act on (e.g. mid-add) — fall through to the toast rather than risk a stale-state DELETE.
    // The branching below treats `reactionId` as non-nullable in the remove path because the
    // guard immediately above returns when willRemove is true and reactionId is missing.
    const reactionId = willRemove ? existing?.yourReactionId : undefined;
    if (willRemove && reactionId === undefined) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.REACTIONS.REMOVE_ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.REACTIONS.ERROR_DETAIL'),
        life: 5000,
      });
      return;
    }
    const optimistic = computeOptimisticReactions(before.reactions, emoji, !willRemove);

    this.applyReactionsPatch(commentId, optimistic);
    this.markRecentlyAuthored(commentId);

    try {
      const updated =
        willRemove && reactionId !== undefined
          ? await this.commentsService.removeReaction({ id: commentId, reactionId })
          : await this.commentsService.addReaction({ id: commentId, emoji });
      // Replace with the canonical aggregate from the server.
      this.applyReactionsPatch(commentId, updated.reactions);
    } catch {
      // Roll back to the pre-action state.
      this.applyReactionsPatch(commentId, before.reactions);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant(
          willRemove ? 'COMMENTS.REACTIONS.REMOVE_ERROR_TITLE' : 'COMMENTS.REACTIONS.ADD_ERROR_TITLE',
        ),
        detail: this.translate.instant('COMMENTS.REACTIONS.ERROR_DETAIL'),
        life: 5000,
      });
    }
  }

  private applyReactionsPatch(commentId: string, reactions: readonly CommentReaction[]): void {
    this.comments.update((prev) =>
      prev.map((c) => (c.id === commentId ? { ...c, reactions: [...reactions] } : c)),
    );
  }

  /**
   * Open the shared FilePreviewDialog for a clicked attachment chip. Previewable rows (image /
   * PDF) get the in-app overlay; non-previewable Office docs route to a direct download via the
   * URL on the attachment payload. Deleted attachments are short-circuited at the chip itself,
   * so we never see a click for them here.
   *
   * The dialog component is loaded lazily — `@tourfold/libraries/file-preview` pulls in
   * ng2-pdf-viewer + marked, which we don't want in the comments hot path.
   */
  protected async onAttachmentOpenRequested(attachment: CommentAttachment): Promise<void> {
    if (attachment.isDeleted) return;
    if (!attachment.isPreviewable) {
      const url = attachment.downloadUrl;
      if (url) window.open(url, '_blank', 'noopener');
      return;
    }
    const fileId = attachment.filestoreFileId;
    const service = this.attachmentsService;
    const { FilePreviewDialogComponent } = await import('@tourfold/libraries/file-preview');
    this.dialogService.open(FilePreviewDialogComponent, {
      showHeader: false,
      width: '90vw',
      height: '90vh',
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      contentStyle: { padding: '0', overflow: 'hidden', height: '100%' },
      data: {
        mimeType: attachment.mimeType,
        name: attachment.name,
        // Re-resolves through the comments backend on every open so a stale list-response URL
        // doesn't break the dialog when the user clicks an hour after the row first rendered.
        // A purged blob surfaces as HTTP 410 `comments/attachment-blob-gone`, which the API
        // client throws — the dialog's catch path renders the "attachment unavailable" state.
        fetchUrl: async () => {
          const fresh = await service.fetchDownloadUrl(fileId);
          return fresh.downloadUrl;
        },
      },
    });
  }

  /**
   * Patch a single row's `isUnread` flag and re-emit the resulting unread count so the
   * instance-detail tab badge stays in sync. Shared by both Mark-read and Mark-unread.
   */
  private flipRead(commentId: string, nextUnread: boolean): void {
    let unreadCount = 0;
    this.comments.update((prev) =>
      prev.map((c) => {
        const next = c.id === commentId ? { ...c, isUnread: nextUnread } : c;
        if (next.isUnread) unreadCount += 1;
        return next;
      }),
    );
    this.unreadCountChange.emit(unreadCount);
  }

  protected onHistoryVisibilityChange(visible: boolean): void {
    if (!visible) this.historyOpenForId.set(null);
  }

  /** Toggle the main composer. Used by the header "New comment" button + the composer's Cancel. */
  protected onToggleComposer(): void {
    this.composerOpen.update((open) => !open);
  }

  /** Wrap `onCreated` so a successful post from the main composer also auto-collapses it. */
  protected onCreatedFromMainComposer(c: Comment): void {
    this.composerOpen.set(false);
    this.onCreated(c);
  }

  /**
   * Inserts the freshly-posted comment into the in-memory list at the right position for the
   * current sort. We do **not** re-fetch the page — the optimistic insert keeps the user's
   * scroll position and avoids losing already-loaded later pages.
   *
   * Threaded mode places replies next to their root's existing siblings; flat mode just
   * prepends (desc) or appends (asc). The next list reload (sort change / target change)
   * will produce the canonical server ordering.
   */
  protected onCreated(c: Comment): void {
    this.markRecentlyAuthored(c.id);
    const isAsc = this.selectedSort().endsWith(':asc');
    const isThreaded = this.isThreaded();

    this.comments.update((prev) => {
      if (c.parentCommentId && isThreaded) {
        // Insert immediately after the last existing sibling on the same root.
        let insertIdx = prev.length;
        for (let i = prev.length - 1; i >= 0; i--) {
          const candidate = prev[i];
          if (candidate && candidate.rootCommentId === c.rootCommentId) {
            insertIdx = i + 1;
            break;
          }
        }
        return [...prev.slice(0, insertIdx), c, ...prev.slice(insertIdx)];
      }
      return isAsc ? [...prev, c] : [c, ...prev];
    });
    this.replyingTo.set(null);
  }

  private async loadFirstPage(
    targetType: string,
    targetId: string,
    sort: Sort,
  ): Promise<void> {
    this.firstLoadCompleted.set(false);
    this.loading.set(true);
    this.hasError.set(false);
    this.comments.set([]);
    this.currentPage.set(0);
    this.totalPages.set(0);
    try {
      const page = await this.commentsService.list({ targetType, targetId, page: 0, sort });
      this.comments.set(page.items);
      this.currentPage.set(page.page.number);
      this.totalPages.set(page.page.totalPages);
      this.unreadCountChange.emit(page.unreadCount);
    } catch {
      this.hasError.set(true);
      this.comments.set([]);
    } finally {
      this.loading.set(false);
      // Flip last so the deep-link effect sees both `loading=false` and `firstLoadCompleted=true`
      // — the same `runEffects` pass picks up both writes and the resolver runs once.
      this.firstLoadCompleted.set(true);
    }
  }

  private async loadNextPage(): Promise<void> {
    this.loadingMore.set(true);
    try {
      const page = await this.commentsService.list({
        targetType: this.targetType(),
        targetId: this.targetId(),
        page: this.currentPage() + 1,
        sort: this.selectedSort(),
      });
      this.comments.update((prev) => [...prev, ...page.items]);
      this.currentPage.set(page.page.number);
      this.totalPages.set(page.page.totalPages);
      this.unreadCountChange.emit(page.unreadCount);
    } catch {
      // Silently keep the existing list; the load-more button stays available so the user can retry.
    } finally {
      this.loadingMore.set(false);
    }
  }

  protected trackByCommentId(_: number, c: Comment): string {
    return c.id;
  }

  ngOnDestroy(): void {
    this.autoReadObserver?.disconnect();
    this.autoReadObserver = null;
    if (this.autoReadTimer !== null) {
      clearTimeout(this.autoReadTimer);
      this.autoReadTimer = null;
    }
  }

  // ─── SSE handling ────────────────────────────────────────────────────────────

  /**
   * Dispatch an incoming SSE event onto the local list. Filtering by `(target_type, target_id)`
   * happens here so we don't need to scope the subscription per-target — the same observable
   * carries every record's events. Local-vs-remote dedup happens after the filter: if the user
   * just performed the action locally, the optimistic update already landed and we skip the
   * event to avoid double-application.
   */
  private onSseEvent(event: CommentEvent): void {
    if (event.targetType !== this.targetType() || event.targetId !== this.targetId()) return;
    // Reactions skip the recentlyAuthoredIds map because the same row legitimately receives many
    // reaction events (different users / emojis) — using the comment-id dedup window would drop
    // valid remote events. We rely on the `reactorId === currentUserId` check below instead.
    if (event.kind !== 'COMMENT_REACTED:V2' && event.kind !== 'COMMENT_UNREACTED:V2') {
      if (this.isRecentlyAuthoredLocally(event.commentId)) return;
    }
    switch (event.kind) {
      case 'COMMENT_CREATED:V2':
        void this.applyRemoteCreated(event);
        return;
      case 'COMMENT_EDITED:V2':
        this.applyRemoteEdited(event);
        return;
      case 'COMMENT_DELETED:V2':
        this.applyRemoteDeleted(event);
        return;
      case 'COMMENT_RESTORED:V2':
        this.applyRemoteRestored(event);
        return;
      case 'COMMENT_REACTED:V2':
        this.applyRemoteReacted(event);
        return;
      case 'COMMENT_UNREACTED:V2':
        this.applyRemoteUnreacted(event);
        return;
    }
  }

  /**
   * Thin path: refetch the freshly-created row so we get the resolved author + caller-scoped
   * `is_unread`, then insert sort-aware via the existing local-create logic. If `getById` fails
   * (network blip, race with deletion), we silently skip — the next list reload corrects state.
   */
  private async applyRemoteCreated(event: CommentCreatedEvent): Promise<void> {
    // Skip if the row is already in the loaded set (rare; defensive against duplicate fan-out).
    if (this.comments().some((c) => c.id === event.commentId)) return;
    let row: Comment;
    try {
      row = await this.commentsService.getById(event.commentId);
    } catch {
      return;
    }
    // Re-check the dedup window after the await — a local action that started while we were
    // fetching may have already inserted the row.
    if (this.isRecentlyAuthoredLocally(event.commentId)) return;
    if (this.comments().some((c) => c.id === event.commentId)) return;
    this.onCreated(row);
    this.flagArrival(row.id);
    this.recomputeUnreadCount();
    if (row.author.id !== this.currentUserId()) {
      const author = `${row.author.firstName} ${row.author.lastName}`.trim()
        || this.translate.instant('COMMENTS.UNKNOWN_AUTHOR');
      this.liveRegionMessage.set(
        this.translate.instant('COMMENTS.LIVE_NEW_COMMENT', { author }),
      );
    }
  }

  /** Thick path: patch body + edited markers in place. */
  private applyRemoteEdited(event: CommentEditedEvent): void {
    this.comments.update((prev) =>
      prev.map((c) =>
        c.id === event.commentId
          ? {
              ...c,
              bodyMarkdown: event.bodyMarkdown,
              lastEditedAt: new Date(event.lastEditedAt),
              isEdited: true,
              isModerated: event.editorId !== c.author.id,
            }
          : c,
      ),
    );
  }

  /** Thick path: flip the row to its tombstone state. */
  private applyRemoteDeleted(event: CommentDeletedEvent): void {
    this.comments.update((prev) =>
      prev.map((c) =>
        c.id === event.commentId
          ? {
              ...c,
              isDeleted: true,
              isDeletedByModerator: event.deletedBy !== c.author.id,
              // Tombstones scrub the body; mirror the server's render path.
              bodyMarkdown: '',
            }
          : c,
      ),
    );
    this.recomputeUnreadCount();
  }

  /** Thick path: clear the tombstone and restore the body the event carries. */
  private applyRemoteRestored(event: CommentRestoredEvent): void {
    this.comments.update((prev) =>
      prev.map((c) =>
        c.id === event.commentId
          ? {
              ...c,
              isDeleted: false,
              isDeletedByModerator: false,
              bodyMarkdown: event.bodyMarkdown,
            }
          : c,
      ),
    );
  }

  /**
   * Reaction added remotely. Skipped when the reactor is the calling user because the optimistic
   * update already landed; replaying it would not change state but would needlessly re-render.
   * Skipped on missing rows (event for a comment outside the current page) and on tombstones.
   */
  private applyRemoteReacted(event: CommentReactedEvent): void {
    if (event.reactorId === this.currentUserId()) return;
    this.comments.update((prev) =>
      prev.map((c) => {
        if (c.id !== event.commentId || c.isDeleted) return c;
        return {
          ...c,
          reactions: computeOptimisticReactions(c.reactions, event.emoji, true, /*isCaller*/ false),
        };
      }),
    );
  }

  /** Mirror of {@link applyRemoteReacted} for the remove direction. */
  private applyRemoteUnreacted(event: CommentUnreactedEvent): void {
    if (event.reactorId === this.currentUserId()) return;
    this.comments.update((prev) =>
      prev.map((c) => {
        if (c.id !== event.commentId || c.isDeleted) return c;
        return {
          ...c,
          reactions: computeOptimisticReactions(c.reactions, event.emoji, false, /*isCaller*/ false),
        };
      }),
    );
  }

  /**
   * Mark `commentId` as authored locally so the matching SSE round-trip is suppressed for the
   * next {@link SSE_DEDUP_WINDOW_MS}. Called from every local write path (create / edit / delete
   * / restore). The window is small enough that a stale entry can't accidentally suppress a real
   * remote action on the same id (which would require a different user on the same comment id —
   * impossible: ids are server-assigned per row).
   */
  private markRecentlyAuthored(commentId: string): void {
    this.recentlyAuthoredIds.set(commentId, Date.now());
  }

  private isRecentlyAuthoredLocally(commentId: string): boolean {
    const at = this.recentlyAuthoredIds.get(commentId);
    if (at === undefined) return false;
    if (Date.now() - at > CommentsComponent.SSE_DEDUP_WINDOW_MS) {
      this.recentlyAuthoredIds.delete(commentId);
      return false;
    }
    return true;
  }

  /**
   * Adds `commentId` to {@link arrivedIds} and clears it after the entry-animation duration so
   * the row settles back into the static layout. The class on the `<li>` toggles purely off this
   * signal, so re-renders during scroll/load-more don't disturb the animation.
   */
  private flagArrival(commentId: string): void {
    this.arrivedIds.update((prev) => {
      const next = new Set(prev);
      next.add(commentId);
      return next;
    });
    setTimeout(() => {
      this.arrivedIds.update((prev) => {
        if (!prev.has(commentId)) return prev;
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
    }, CommentsComponent.ARRIVAL_ANIMATION_MS);
  }

  /**
   * Re-emit the current unread count after a list mutation. Used by the SSE handlers so the tab
   * pill stays in sync without the server having to re-confirm via `unreadCountForTarget`.
   */
  private recomputeUnreadCount(): void {
    const count = this.comments().reduce((acc, c) => acc + (c.isUnread ? 1 : 0), 0);
    this.unreadCountChange.emit(count);
  }

  // ─── Deep-link resolution ────────────────────────────────────────────────────

  /**
   * Loads the comment with [id] into the visible list and applies the focus highlight. If the
   * row is already in `comments()` (deep-linked to a recently-loaded comment) we go straight to
   * `applyFocus` — no extra round trips. Otherwise:
   *   1. `getPageInfo` resolves which page contains the row under the current sort + size.
   *   2. We page through the gap with `loadNextPage()` until `currentPage()` reaches the target.
   *   3. The row is now in the list — focus it.
   *
   * Failures (404, target mismatch, network error) surface a non-blocking toast and leave the
   * list in its previous state. The user can still see the comment by scrolling / paginating
   * manually; the deep link just didn't fast-forward.
   */
  private async resolveDeepLink(id: string): Promise<void> {
    if (this.comments().some((c) => c.id === id)) {
      this.applyFocus(id);
      return;
    }
    try {
      const info = await this.commentsService.getPageInfo({ id, sort: this.selectedSort() });
      // Page through to the target. `loadNextPage` advances `currentPage`; bail if a sibling
      // process (e.g. a sort change) reset it backwards or commentId changed mid-load.
      while (this.currentPage() < info.page) {
        const before = this.currentPage();
        await this.loadNextPage();
        if (this.currentPage() === before) break; // didn't advance — abandon to avoid an infinite loop
        if (this.commentId() !== id) return; // input changed during paging; let the next effect handle it
      }
      if (this.comments().some((c) => c.id === id)) {
        this.applyFocus(id);
      } else {
        // Row not in the page we just loaded — almost certainly a different target / cross-record
        // link. Fall back to the single-comment endpoint and treat it as an SSE-style insert at
        // the top so the user still sees what was linked.
        try {
          const row = await this.commentsService.getById(id);
          if (row.targetType !== this.targetType() || row.targetId !== this.targetId()) {
            this.notifyDeepLinkNotFound();
            return;
          }
          this.onCreated(row);
          this.applyFocus(id);
        } catch {
          this.notifyDeepLinkNotFound();
        }
      }
    } catch {
      this.notifyDeepLinkNotFound();
    }
  }

  /**
   * Sets the focus highlight on [id] and scrolls the row into view. The class on the `<li>`
   * derives from `focusedCommentId() === id`; clearing the signal after
   * {@link FOCUS_HIGHLIGHT_MS} restores the row to its normal state.
   */
  private applyFocus(id: string): void {
    this.focusedCommentId.set(id);
    // Wait for Angular's change detection to flush the new comment list into the DOM before
    // scrolling. queueMicrotask isn't enough on the cross-page deep-link path: when
    // `resolveDeepLink` has just appended a freshly-loaded page via `loadNextPage`, the signal
    // write completes inside the current microtask but Angular's render reaction runs on a
    // later macrotask. Two animation frames give CD time to land the new `<li>` before we look
    // it up — the scroll then targets the actual row instead of the stale tail.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const list = this.listEl()?.nativeElement;
      if (!list) return;
      const el = list.querySelector(`li[data-comment-id="${CSS.escape(id)}"]`);
      if (el instanceof HTMLElement) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }));
    setTimeout(() => {
      if (this.focusedCommentId() === id) this.focusedCommentId.set(null);
    }, CommentsComponent.FOCUS_HIGHLIGHT_MS);
  }

  private notifyDeepLinkNotFound(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('COMMENTS.DEEP_LINK.NOT_FOUND_TITLE'),
      detail: this.translate.instant('COMMENTS.DEEP_LINK.NOT_FOUND_DETAIL'),
      life: 4000,
    });
  }

  /**
   * Lazily create an `IntersectionObserver` (one per component instance) and (re-)observe every
   * `<li>` row currently rendered in the list. The observer's callback updates `visibleIds`
   * and reschedules the debounced `flushAutoRead` call. We re-attach on every list change so
   * newly-rendered rows after load-more / sort-change / SSE arrival are picked up.
   */
  private attachAutoReadObserver(listEl: HTMLElement): void {
    if (typeof IntersectionObserver === 'undefined') return;
    // 50% threshold: a row counts as "seen" once half of it crosses into the viewport. Lower
    // bars (e.g. 0.1) caused premature mark-reads when the user only scrolled by a couple of
    // pixels and the row's top edge clipped in.
    this.autoReadObserver ??= new IntersectionObserver(
      (entries) => this.onIntersectionChange(entries),
      { threshold: 0.5 },
    );
    // Observe all currently-rendered rows. Previously-observed rows that have been torn down
    // by Angular's `@for` will auto-unobserve when their DOM node is disconnected.
    const rows = listEl.querySelectorAll<HTMLElement>('li[data-comment-id]');
    rows.forEach((el) => this.autoReadObserver?.observe(el));
  }

  private onIntersectionChange(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const id = (entry.target as HTMLElement).dataset['commentId'];
      if (!id) continue;
      if (entry.isIntersecting) {
        this.visibleIds.add(id);
      } else {
        this.visibleIds.delete(id);
      }
    }
    this.scheduleAutoRead();
  }

  /**
   * Resets the debounce timer on every visibility change. When the dust settles for
   * {@link AUTO_READ_DEBOUNCE_MS} we run `flushAutoRead`. The "settle" semantics keep us from
   * spamming the server while the user is actively scrolling.
   */
  private scheduleAutoRead(): void {
    if (this.autoReadTimer !== null) clearTimeout(this.autoReadTimer);
    this.autoReadTimer = setTimeout(
      () => {
        this.autoReadTimer = null;
        void this.flushAutoRead();
      },
      CommentsComponent.AUTO_READ_DEBOUNCE_MS,
    );
  }

  /**
   * Pick the newest visible row whose `createdAt` is strictly after the last advanced cursor
   * and call `markCommentsReadUntil` with its id. **Sticky-unread UX (GitHub / Linear / Slack
   * pattern):** the server cursor advances and the tab-header badge clears, but the per-row
   * visual treatment (`isUnread` flag) stays put until the next fresh list-fetch (sort change,
   * target change, page reload). Without this, the rows would visibly flip to "read" 1.5 s
   * after the user opens the tab — feels like a bug, makes it impossible to actually scan
   * what's new.
   *
   * No-op cases:
   *  - No visible rows (e.g. user scrolled away from the panel).
   *  - The newest visible row's `createdAt` is `<=` `lastReadCursorAt` — already covered.
   *  - No unread rows in the local list — nothing to advance over.
   */
  private async flushAutoRead(): Promise<void> {
    const list = this.comments();
    if (list.length === 0 || this.visibleIds.size === 0) return;
    if (!list.some((c) => c.isUnread)) return;
    let newest: Comment | null = null;
    for (const c of list) {
      if (!this.visibleIds.has(c.id)) continue;
      const ts = c.createdAt.getTime();
      if (newest === null || ts > newest.createdAt.getTime()) newest = c;
    }
    if (!newest) return;
    const newestTs = newest.createdAt.getTime();
    if (this.lastReadCursorAt !== null && newestTs <= this.lastReadCursorAt) return;
    const cursor = newest;
    this.lastReadCursorAt = newestTs;
    try {
      await this.commentsService.markReadUntil({
        targetType: this.targetType(),
        targetId: this.targetId(),
        commentId: cursor.id,
      });
      // Drop the badge to zero — the tab-header pill clears immediately. Per-row `isUnread`
      // visuals deliberately stay (sticky-unread).
      this.unreadCountChange.emit(0);
    } catch {
      // Best-effort: the next visibility change will retry. Don't surface a toast — auto-mark-
      // read is a background convenience, not an explicit user action.
      this.lastReadCursorAt = null;
    }
  }
}

/**
 * Pure helper: computes the next reactions array for a single emoji toggle. Used by both the
 * local optimistic-update path and the remote SSE patch path.
 *
 * Insertion order matches the server (`MIN(created_at)` per `(comment_id, emoji)`): an emoji
 * that wasn't in the list before is appended at the end; an existing emoji keeps its position
 * (even when its count is dropped to 0, the row is removed and the surviving emojis keep their
 * relative order).
 *
 * Caller is identified by [isCaller]:
 *  - **true** (default — the calling user is reacting): `youReacted` is set to the post-toggle
 *    boolean, `count` shifts by ±1 from the caller's perspective.
 *  - **false** (a remote user is reacting): `youReacted` is preserved, `count` shifts by ±1 to
 *    reflect the remote user's action.
 *
 * Removing the last reaction with that emoji drops the entry from the list (no zero-count rows
 * on the wire). Re-adding it inserts at the end.
 */
export function computeOptimisticReactions(
  current: readonly CommentReaction[],
  emoji: string,
  add: boolean,
  isCaller: boolean = true,
): CommentReaction[] {
  const existing = current.find((r) => r.emoji === emoji);
  if (add) {
    if (!existing) {
      return [
        ...current,
        { emoji, count: 1, youReacted: isCaller },
      ];
    }
    // For the caller path, an "add" on something we already reacted to is a no-op (the toggle
    // path routes through remove instead) — but for a remote add by another user the count
    // should still bump regardless of `youReacted`. Bump always; the caller path won't reach
    // this branch in practice because the dispatch decides add vs remove from `youReacted`.
    return current.map((r) =>
      r.emoji === emoji
        ? { emoji, count: r.count + 1, youReacted: isCaller ? true : r.youReacted }
        : r,
    );
  }
  // Remove direction.
  if (!existing) return [...current];
  const nextCount = existing.count - 1;
  if (nextCount <= 0) {
    return current.filter((r) => r.emoji !== emoji);
  }
  return current.map((r) =>
    r.emoji === emoji
      ? { emoji, count: nextCount, youReacted: isCaller ? false : r.youReacted }
      : r,
  );
}
